import { useState, useEffect } from 'react';
import { createTask, updateTask } from '../../utils/api';
import { useApp } from '../../context/AppContext';

const DURATION_OPTIONS = [
  { label: '—', value: '' },
  { label: '15 min', value: 15 },
  { label: '30 min', value: 30 },
  { label: '45 min', value: 45 },
  { label: '1 hour', value: 60 },
  { label: '1.5 hours', value: 90 },
  { label: '2 hours', value: 120 },
  { label: '3 hours', value: 180 },
  { label: 'Half day (4h)', value: 240 },
  { label: 'Full day (8h)', value: 480 },
];

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const defaultForm = {
  title: '',
  description: '',
  category: 'general',
  frequency: 'monthly',
  customFrequencyDays: '',
  nextDue: '',
  estimatedMinutes: '',
  notes: '',
  linkedAppliance: '',
  scheduledMonths: [],
};

const TaskForm = ({ existing, onClose, onSaved }) => {
  const { fetchTasks, appliances } = useApp();
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (existing) {
      setForm({
        ...existing,
        nextDue: existing.nextDue?.slice(0, 10) ?? '',
        customFrequencyDays: existing.customFrequencyDays ?? '',
        estimatedMinutes: existing.estimatedMinutes ?? '',
        linkedAppliance: existing.linkedAppliance ?? '',
        scheduledMonths: existing.scheduledMonths ?? [],
      });
    }
  }, [existing]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleMonth = (m) => {
    setForm((prev) => ({
      ...prev,
      scheduledMonths: prev.scheduledMonths.includes(m)
        ? prev.scheduledMonths.filter((x) => x !== m)
        : [...prev.scheduledMonths, m],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (existing) {
        await updateTask(existing._id, form);
      } else {
        await createTask(form);
      }
      await fetchTasks({ force: true });
      onSaved?.();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{existing ? 'Edit Task' : 'New Task'}</h2>
        <form onSubmit={handleSubmit} className="form">
          <label>Title *
            <input name="title" value={form.title} onChange={handleChange} required />
          </label>
          <label>Description
            <textarea name="description" value={form.description} onChange={handleChange} rows={2} />
          </label>
          <div className="form-row">
            <label>Category
              <select name="category" value={form.category} onChange={handleChange}>
                {['hvac', 'plumbing', 'electrical', 'exterior', 'appliance', 'automotive', 'general'].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
            <label>Frequency *
              <select name="frequency" value={form.frequency} onChange={handleChange}>
                {['weekly', 'monthly', 'quarterly', 'biannual', 'annual', 'custom'].map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </label>
          </div>
          {form.frequency === 'custom' && (
            <label>Custom Frequency (days)
              <input
                type="number"
                name="customFrequencyDays"
                value={form.customFrequencyDays}
                onChange={handleChange}
                min={1}
              />
            </label>
          )}
          <div className="form-row">
            <label>Next Due Date *
              <input type="date" name="nextDue" value={form.nextDue} onChange={handleChange} required />
            </label>
            <label>Estimated Time
              <select name="estimatedMinutes" value={form.estimatedMinutes} onChange={handleChange}>
                {DURATION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
          </div>
          <div>
            <label style={{ marginBottom: '0.4rem', display: 'block' }}>
              Scheduled Months
              <span style={{ fontWeight: 400, color: 'var(--muted)', marginLeft: '0.4rem', fontSize: '0.82rem' }}>
                — overrides frequency; due date jumps to the next selected month after completion
              </span>
            </label>
            <div className="month-grid">
              {MONTHS.map((label, i) => {
                const m = i + 1;
                const active = form.scheduledMonths.includes(m);
                return (
                  <button
                    key={m}
                    type="button"
                    className={`month-chip${active ? ' month-chip-active' : ''}`}
                    onClick={() => toggleMonth(m)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          <label>Notes
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={2} />
          </label>
          {appliances.length > 0 && (
            <label>Linked Appliance
              <select name="linkedAppliance" value={form.linkedAppliance} onChange={handleChange}>
                <option value="">— None —</option>
                {appliances.map((a) => (
                  <option key={a._id} value={a._id}>{a.name}</option>
                ))}
              </select>
            </label>
          )}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskForm;
